'use client';

import { Capacitor } from '@capacitor/core';
import axios from 'axios';
import { useRouter } from 'next/navigation';
import { useSession } from 'next-auth/react';
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

const PENDING_DEEP_LINK_KEY = 'mobile.pendingDeepLink';
const PROTECTED_ROUTE_PREFIXES = ['/profilom', '/szervezom', '/hirdetoi-fiok'];

function resolveInternalRoute(target) {
    if (!target || typeof target !== 'string' || typeof window === 'undefined') return null;

    try {
        const url = new URL(target, window.location.origin);
        const trustedHosts = new Set([window.location.host, 'eskuvohelyszin.hu', 'www.eskuvohelyszin.hu']);
        if (!trustedHosts.has(url.host)) return null;

        return `${url.pathname}${url.search}${url.hash}`;
    } catch (_) {
        return null;
    }
}

function getNotificationTarget(notification) {
    const data = notification?.data || notification?.notification?.data || {};
    return data.path || data.url || data.deeplink || null;
}

async function getNativeDeviceInfo() {
    const { Device } = await import('@capacitor/device');
    const [info, id] = await Promise.all([Device.getInfo(), Device.getId()]);
    return { ...info, identifier: id?.identifier || '' };
}

async function initNativeShell() {
    try {
        const { StatusBar, Style } = await import('@capacitor/status-bar');
        // reserve dedicated space for the status bar instead of overlaying the page content
        await StatusBar.setOverlaysWebView({ overlay: false });
        await StatusBar.setStyle({ style: Style.Dark });
        if (Capacitor.getPlatform() === 'android') {
            await StatusBar.setBackgroundColor({ color: '#ffffff' });
        }
    } catch (error) {
        console.warn('StatusBar init failed', error);
    }

    try {
        const { SplashScreen } = await import('@capacitor/splash-screen');
        await SplashScreen.hide();
    } catch (error) {
        console.warn('SplashScreen hide failed', error);
    }

    try {
        const { App } = await import('@capacitor/app');
        const listener = await App.addListener('backButton', ({ canGoBack }) => {
            const openOverlay = document.querySelector('.modal.show, .offcanvas.show');
            if (openOverlay) {
                openOverlay.dispatchEvent(new KeyboardEvent('keydown', {
                    key: 'Escape',
                    code: 'Escape',
                    bubbles: true,
                }));
                return;
            }

            if (canGoBack) {
                window.history.back();
            } else {
                App.exitApp();
            }
        });
        return () => listener.remove();
    } catch (error) {
        console.warn('Back button handler init failed', error);
        return () => {};
    }
}

export const MobileAppContext = createContext({
    isNativeApp: false,
    isOnline: true,
    platform: 'web',
    pushStatus: 'idle',
    isPushSupported: false,
    pushToken: '',
    deviceInfo: {},
    registerPush: async () => {},
    setStatusBarTheme: async () => {},
});

export function MobileAppProvider({ children }) {
    const router = useRouter();
    const [isNativeApp, setIsNativeApp] = useState(false);
    const [isOnline, setIsOnline] = useState(true);
    const [platform, setPlatform] = useState('web');
    const [pushStatus, setPushStatus] = useState('idle');
    const [isPushSupported, setIsPushSupported] = useState(false);
    const [pushToken, setPushToken] = useState('');
    const [deviceInfo, setDeviceInfo] = useState({});
    const didSyncPushRef = useRef(false);
    const { data: sessionData, status: sessionStatus } = useSession();
    const authToken = sessionData?.user?.token || '';

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const openAppRoute = (target) => {
            const route = resolveInternalRoute(target);
            if (!route) return;

            const requiresAuth = PROTECTED_ROUTE_PREFIXES.some((prefix) => route.startsWith(prefix));
            if (requiresAuth && sessionStatus !== 'authenticated') {
                try { window.sessionStorage.setItem(PENDING_DEEP_LINK_KEY, route); } catch (_) {}
                router.push(route.startsWith('/hirdetoi-fiok') ? '/szolgaltato-bejelentkezes' : '/bejelentkezes');
                return;
            }

            router.push(route);
        };

        const native = Boolean(Capacitor?.isNativePlatform?.());
        setIsNativeApp(native);
        setPlatform(Capacitor?.getPlatform?.() ?? 'web');

        if (!native) {
            setPushStatus('web');
            setIsPushSupported(false);
            return;
        }

        document.documentElement.classList.add('capacitor-native');
        document.body?.classList.add('capacitor-native');
        setIsPushSupported(true);

        const updateOnlineStatus = () => setIsOnline(window.navigator.onLine);
        updateOnlineStatus();
        window.addEventListener('online', updateOnlineStatus);
        window.addEventListener('offline', updateOnlineStatus);

        let removeBackButtonListener = () => {};
        let nativeListeners = [];
        let disposed = false;
        initNativeShell().then((cleanup) => {
            if (typeof cleanup !== 'function') return;
            if (disposed) cleanup();
            else removeBackButtonListener = cleanup;
        });

        const initializePush = async () => {
            try {
                const device = await getNativeDeviceInfo();
                setDeviceInfo(device);

                const pluginModule = await import('@capacitor/push-notifications');
                const PushNotifications = pluginModule.PushNotifications;

                if (!PushNotifications?.requestPermissions || !PushNotifications?.register) {
                    setPushStatus('unsupported');
                    return;
                }

                const pushListeners = await Promise.all([
                    PushNotifications.addListener('registration', ({ value }) => {
                        setPushToken(value || '');
                        setPushStatus(value ? 'registered' : 'error');
                    }),
                    PushNotifications.addListener('registrationError', (error) => {
                        console.warn('Capacitor push registration failed', error);
                        setPushStatus('error');
                    }),
                    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
                        openAppRoute(getNotificationTarget(action));
                    }),
                ]);
                if (disposed) pushListeners.forEach((listener) => listener.remove());
                else nativeListeners.push(...pushListeners);

                if (process.env.NEXT_PUBLIC_PUSH_ENABLED !== 'true') {
                    setPushStatus('unsupported');
                    return;
                }

                const permission = await PushNotifications.checkPermissions();
                if (permission.receive === 'granted') {
                    setPushStatus('requesting');
                    await PushNotifications.register();
                } else {
                    setPushStatus(permission.receive === 'denied' ? 'denied' : 'idle');
                }
            } catch (error) {
                console.warn('Capacitor push initialization failed', error);
                setPushStatus('error');
            }
        };

        const initializeAppLinks = async () => {
            try {
                const { App } = await import('@capacitor/app');
                const listener = await App.addListener('appUrlOpen', ({ url }) => openAppRoute(url));
                if (disposed) listener.remove();
                else nativeListeners.push(listener);

                const launchUrl = await App.getLaunchUrl();
                if (!disposed && launchUrl?.url) openAppRoute(launchUrl.url);
            } catch (error) {
                console.warn('Capacitor app link initialization failed', error);
            }
        };

        initializePush();
        initializeAppLinks();

        return () => {
            disposed = true;
            removeBackButtonListener();
            nativeListeners.forEach((listener) => listener.remove());
            window.removeEventListener('online', updateOnlineStatus);
            window.removeEventListener('offline', updateOnlineStatus);
        };
    }, [router, sessionStatus]);

    useEffect(() => {
        if (sessionStatus !== 'authenticated') return;

        try {
            const pendingRoute = window.sessionStorage.getItem(PENDING_DEEP_LINK_KEY);
            if (!pendingRoute) return;

            window.sessionStorage.removeItem(PENDING_DEEP_LINK_KEY);
            router.replace(pendingRoute);
        } catch (_) {}
    }, [router, sessionStatus]);

    useEffect(() => {
        if (!isNativeApp || !pushToken || pushStatus !== 'registered' || !authToken || didSyncPushRef.current) {
            return;
        }

        didSyncPushRef.current = true;

        const syncPushToken = async () => {
            try {
                await axios.post(`${process.env.NEXT_PUBLIC_BACKEND_URL}/auth/register_push_token`, {
                    token: pushToken,
                    platform,
                    device_id: deviceInfo?.identifier || deviceInfo?.model || '',
                    device_info: deviceInfo,
                }, {
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                        'Content-Type': 'application/json',
                    },
                });
            } catch (error) {
                console.warn('Push token sync failed', error);
                didSyncPushRef.current = false;
            }
        };

        syncPushToken();
    }, [authToken, deviceInfo, isNativeApp, platform, pushStatus, pushToken]);

    const value = useMemo(() => ({
        isNativeApp,
        isOnline,
        platform,
        pushStatus,
        isPushSupported,
        pushToken,
        deviceInfo,
        // lets native-only fullscreen overlays (e.g. image gallery) sync the status bar to a dark/light backdrop
        setStatusBarTheme: async (theme = 'light') => {
            if (!isNativeApp) return;
            try {
                const { StatusBar, Style } = await import('@capacitor/status-bar');
                const isDark = theme === 'dark';
                await StatusBar.setStyle({ style: isDark ? Style.Light : Style.Dark });
                if (Capacitor.getPlatform() === 'android') {
                    await StatusBar.setBackgroundColor({ color: isDark ? '#000000' : '#ffffff' });
                }
            } catch (error) {
                console.warn('StatusBar theme change failed', error);
            }
        },
        registerPush: async () => {
            didSyncPushRef.current = false;

            if (!isNativeApp) {
                setPushStatus('web');
                setIsPushSupported(false);
                setPushToken('');
                setDeviceInfo({});
                return;
            }

            setPushStatus('requesting');
            try {
                const device = await getNativeDeviceInfo();
                setDeviceInfo(device);

                const pluginModule = await import('@capacitor/push-notifications');
                const PushNotifications = pluginModule.PushNotifications;
                const permission = await PushNotifications.requestPermissions();

                if (permission.receive !== 'granted') {
                    setPushStatus('denied');
                    return;
                }

                if (process.env.NEXT_PUBLIC_PUSH_ENABLED !== 'true') {
                    setPushStatus('unsupported');
                    return;
                }

                await PushNotifications.register();
            } catch (error) {
                console.warn('Capacitor push registration failed', error);
                setPushStatus('error');
            }
        },
    }), [deviceInfo, isNativeApp, isOnline, isPushSupported, platform, pushStatus, pushToken]);

    return (
        <MobileAppContext.Provider value={value}>
            {children}
        </MobileAppContext.Provider>
    );
}

export function useMobileApp() {
    return useContext(MobileAppContext);
}
